import React from "react";
import inc from "ramda/src/inc";
import dec from "ramda/src/dec";
import range from "ramda/src/range";
import { SxProps } from "@mui/system";

import ChevronRightLargeIcon from "@atlaskit/icon/glyph/chevron-right-large";
import ChevronLeftLargeIcon from "@atlaskit/icon/glyph/chevron-left-large";

import { Box } from "../../UI/Box";
import { Grid } from "../../UI/Grid";
import { ISliderQuestion } from "../../constants/types";
import {
  COLOR_ATHENS_GRAY_DARKER,
  COLOR_BLACK,
  COLOR_SCIENCE_BLUE,
  COLOR_STALE_GRAY,
  COLOR_WHITE,
} from "../../constants/styles";

import { QuestionLayout } from "./QuestionLayout";

const getQuestionSx = (userAnswers: number, idx: number): SxProps => ({
  fontSize: 12,
  height: 32,
  fontWeight: 600,
  border: `1px solid ${COLOR_SCIENCE_BLUE}`,
  color: userAnswers === inc(idx) ? COLOR_WHITE : COLOR_BLACK,
  bgcolor: userAnswers === inc(idx) ? COLOR_SCIENCE_BLUE : "transparent",
  borderRightWidth: 0,
  "&:first-child": {
    borderTopLeftRadius: 20,
    borderBottomLeftRadius: 20,
  },
  "&:last-child": {
    borderTopRightRadius: 20,
    borderBottomRightRadius: 20,
    borderRightWidth: 1,
  },
});

const Question: React.FC<Pick<ISliderQuestion, "numberOfSliderOptions" | "userAnswers">> = (props) => (
  <Grid container justifyContent="space-around" wrap="nowrap">
    {range(0, props.numberOfSliderOptions).map((idx) => (
      <Grid
        xs
        item
        key={idx}
        display="flex"
        alignItems="center"
        justifyContent="center"
        sx={getQuestionSx(props.userAnswers, idx)}
      >
        {inc(idx)}
      </Grid>
    ))}
  </Grid>
);

const SliderThumb: React.FC = (props) => (
  <Box
    top={-8}
    borderRadius={24}
    ml={-1}
    bgcolor={COLOR_WHITE}
    boxShadow="0px 3px 5px rgba(9, 30, 66, 0.2), 0px 0px 1px rgba(9, 30, 66, 0.31)"
    position="absolute"
    height={24}
    width={24}
    display="flex"
    alignItems="center"
    justifyContent="center"
    {...props}
  />
);

const AnswerRange: React.FC<
  Pick<ISliderQuestion, "roommateLowAnswers" | "roommateHighAnswers" | "numberOfSliderOptions">
> = (props) => (
  <Grid container justifyContent="space-between" wrap="nowrap" position="relative">
    <Box
      position="absolute"
      height={4}
      top={2}
      left={2}
      right={2}
      bgcolor={COLOR_ATHENS_GRAY_DARKER}
      borderRadius={3}
    />
    <Box
      position="absolute"
      height={4}
      top={2}
      left={`${(dec(props.roommateLowAnswers) / dec(props.numberOfSliderOptions)) * 100}%`}
      right={`${100 - (dec(props.roommateHighAnswers) / dec(props.numberOfSliderOptions)) * 100}%`}
      bgcolor={COLOR_SCIENCE_BLUE}
      borderRadius={3}
    />
    {range(0, props.numberOfSliderOptions).map((idx) => {
      const lowSelected = inc(idx) === props.roommateLowAnswers;
      const highSelected = inc(idx) === props.roommateHighAnswers;
      const selected = lowSelected || highSelected;

      return (
        <Grid item key={idx}>
          <Box
            position="relative"
            height={8}
            width={8}
            borderRadius={8}
            bgcolor={
              inc(idx) >= props.roommateLowAnswers && inc(idx) <= props.roommateHighAnswers
                ? COLOR_SCIENCE_BLUE
                : COLOR_STALE_GRAY
            }
            mb={2}
          />
          {selected && (
            <SliderThumb>
              {lowSelected && <ChevronLeftLargeIcon size="small" label="left" />}
              {highSelected && <ChevronRightLargeIcon size="small" label="left" />}
            </SliderThumb>
          )}
          {inc(idx)}
        </Grid>
      );
    })}
  </Grid>
);

interface IProps extends ISliderQuestion {
  height?: number;
}

export const SliderQuestion: React.FC<IProps> = (props) => (
  <QuestionLayout
    isAnswerLabels
    height={props.height as number}
    answers={props.answers}
    title={props.questionText}
    question={<Question userAnswers={props.userAnswers} numberOfSliderOptions={props.numberOfSliderOptions} />}
    respond={
      <AnswerRange
        roommateHighAnswers={props.roommateHighAnswers}
        roommateLowAnswers={props.roommateLowAnswers}
        numberOfSliderOptions={props.numberOfSliderOptions}
      />
    }
  />
);

SliderQuestion.defaultProps = {
  roommateLowAnswers: 1,
  roommateHighAnswers: 5,
  height: 56,
};
